import { useEffect, useState } from 'react'
import { isStandalone } from '../offline/storage'
import { onSyncCounts, type SyncCounts } from '../offline/sync'

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))

  useEffect(() => {
    const up = (): void => setOnline(true)
    const down = (): void => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  return online
}

export function useSyncCounts(): SyncCounts {
  const [counts, setCounts] = useState<SyncCounts>({ pending: 0, inflight: 0, failed: 0, synced: 0 })
  useEffect(() => onSyncCounts(setCounts), [])
  return counts
}

/**
 * §11: a repaint ticker, and nothing more. It is never the source of truth for
 * remaining time — the components that use it recompute from timestamps on
 * every render, and additionally on visibilitychange, focus, pageshow and online.
 */
export function useRepaintTick(intervalMs = 500, enabled = true): number {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!enabled) return
    const bump = (): void => setTick((t) => t + 1)
    const timer = setInterval(bump, intervalMs)

    // The events that matter more than the interval, because a throttled tab
    // fires the interval a few times a minute at most.
    document.addEventListener('visibilitychange', bump)
    window.addEventListener('focus', bump)
    window.addEventListener('pageshow', bump)
    window.addEventListener('online', bump)

    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', bump)
      window.removeEventListener('focus', bump)
      window.removeEventListener('pageshow', bump)
      window.removeEventListener('online', bump)
    }
  }, [intervalMs, enabled])

  return tick
}

export function useStandalone(): boolean {
  const [standalone] = useState(isStandalone)
  return standalone
}
